/**
 * Новая версия приложения (04.10.2026). Аделина прислала отчёт со старыми карточками игроков
 * («+ телефон», «+ свой», «+ данные следующего игрока»), убранными ещё 28.08: свёрнутое мини-приложение
 * Telegram живёт со старой сборкой, пока его не закроют, — а мы выкладываем по несколько раз в день.
 *
 * Сверяем имя бандла в свежем index.html (без кэша) с загруженным: при старте, раз в 10 минут и при
 * возврате в приложение. Есть новее — плашка «Вышла новая версия · Обновить».
 * Сами обновляем только на старте (Telegram отдал старую сборку из кэша, человек ещё ничего не начал) —
 * один раз на версию, чтобы не зациклиться. Посреди работы — только плашка: недописанный отчёт до
 * «Сохранить», график доступности и расстановка живут в памяти, перезагрузка их бы стёрла.
 * В режиме разработки (vite) не работает: там бандла нет.
 */
import { useEffect, useState } from "react";
import css from "./AppUpdate.module.css";

const CHECK_MS = 10 * 60_000;
const STARTUP_MS = 15_000;
const BUNDLE_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

function loadedBundle(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  if (!s) return null;
  const m = new URL(s.src, location.href).pathname.match(BUNDLE_RE);
  return m ? m[0] : null;
}

async function latestBundle(): Promise<string | null> {
  const r = await fetch(`/?v=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
  if (!r.ok) return null;
  const m = (await r.text()).match(BUNDLE_RE);
  return m ? m[0] : null;
}

/** Перезагрузка мимо кэша: метка в адресе даёт свежий index.html; хэш (данные входа Telegram) сохраняется. */
function hardReload(): void {
  const u = new URL(location.href);
  u.searchParams.set("_v", Date.now().toString(36));
  location.replace(u.toString());
}

type TgEvents = { onEvent?: (e: string, cb: () => void) => void; offEvent?: (e: string, cb: () => void) => void };

export function AppUpdate() {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    if (import.meta.env.DEV) return;
    const mine = loadedBundle();
    if (!mine) return;
    let alive = true;
    const startedAt = Date.now();
    let touched = false;
    const touch = () => { touched = true; };
    /* обновить сразу можно, пока человек ничего не трогал и не открыл форму; на каждую версию — один раз */
    const reloadIfFresh = (latest: string): boolean => {
      if (touched || Date.now() - startedAt > STARTUP_MS || document.querySelector('[role="dialog"]')) return false;
      try {
        if (sessionStorage.getItem("ub_app_reload") === latest) return false;
        sessionStorage.setItem("ub_app_reload", latest);
      } catch { return false; }
      hardReload();
      return true;
    };
    const check = async () => {
      try {
        const latest = await latestBundle();
        if (!alive || !latest || latest === mine) return;
        if (!reloadIfFresh(latest)) setStale(true);
      } catch { /* нет связи — проверим в следующий раз */ }
    };
    const back = () => { void check(); };
    const onVis = () => { if (document.visibilityState === "visible") back(); };
    /* Telegram: развернули мини-приложение — событие SDK (Bot API 8.0), visibilitychange — запасной путь */
    const tg = (window as unknown as { Telegram?: { WebApp?: TgEvents } }).Telegram?.WebApp;
    void check();
    const timer = setInterval(() => { void check(); }, CHECK_MS);
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("pointerdown", touch, true);
    document.addEventListener("keydown", touch, true);
    tg?.onEvent?.("activated", back);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("pointerdown", touch, true);
      document.removeEventListener("keydown", touch, true);
      tg?.offEvent?.("activated", back);
    };
  }, []);
  if (!stale) return null;
  return (
    <button type="button" className={css.bar} onClick={hardReload}>
      Вышла новая версия · <b>Обновить</b>
    </button>
  );
}
